import { db } from '@/firebase/config'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'

const RECORDS_COLLECTION = 'menstrual_records'
const SETTINGS_COLLECTION = 'menstrual_settings'

// =============== Menstrual Records ===============

/**
 * Add a new menstrual record
 */
export const addMenstrualRecord = async (userId, recordData) => {
  try {
    const record = {
      user_id: userId,
      start_date: Timestamp.fromDate(new Date(recordData.start_date)),
      end_date: recordData.end_date ? Timestamp.fromDate(new Date(recordData.end_date)) : null,
      flow_level: recordData.flow_level || 'medium',
      symptoms: recordData.symptoms || [],
      mood: recordData.mood || '',
      note: recordData.note || '',
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, RECORDS_COLLECTION), record)
    return { id: docRef.id, ...record }
  } catch (error) {
    console.error('Error adding menstrual record:', error)
    throw error
  }
}

/**
 * Update an existing menstrual record
 */
export const updateMenstrualRecord = async (recordId, recordData) => {
  try {
    const updateData = {
      ...recordData,
      updated_at: serverTimestamp()
    }

    // Convert dates to Timestamps if provided
    if (recordData.start_date) {
      updateData.start_date = Timestamp.fromDate(new Date(recordData.start_date))
    }
    if (recordData.end_date) {
      updateData.end_date = Timestamp.fromDate(new Date(recordData.end_date))
    }

    await updateDoc(doc(db, RECORDS_COLLECTION, recordId), updateData)
    return { id: recordId, ...updateData }
  } catch (error) {
    console.error('Error updating menstrual record:', error)
    throw error
  }
}

/**
 * Delete a menstrual record
 */
export const deleteMenstrualRecord = async (recordId) => {
  try {
    await deleteDoc(doc(db, RECORDS_COLLECTION, recordId))
  } catch (error) {
    console.error('Error deleting menstrual record:', error)
    throw error
  }
}

/**
 * Get all menstrual records for a user
 */
export const getUserMenstrualRecords = async (userId, months = 6) => {
  try {
    const q = query(
      collection(db, RECORDS_COLLECTION),
      where('user_id', '==', userId)
    )

    const querySnapshot = await getDocs(q)
    const records = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    // Sort by start_date descending in JavaScript
    records.sort((a, b) => {
      const dateA = a.start_date?.seconds || 0
      const dateB = b.start_date?.seconds || 0
      return dateB - dateA
    })

    // Filter by months if specified
    if (months > 0) {
      const monthsAgo = new Date()
      monthsAgo.setMonth(monthsAgo.getMonth() - months)

      return records.filter(record => {
        const recordDate = record.start_date?.toDate() || new Date(0)
        return recordDate >= monthsAgo
      })
    }

    return records
  } catch (error) {
    console.error('Error getting menstrual records:', error)
    throw error
  }
}

/**
 * Get a single menstrual record
 */
export const getMenstrualRecord = async (recordId) => {
  try {
    const docRef = doc(db, RECORDS_COLLECTION, recordId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting menstrual record:', error)
    throw error
  }
}

// =============== Cycle Prediction ===============

/**
 * Calculate cycle predictions based on historical data
 */
export const calculateCyclePredictions = (records) => {
  if (!records || records.length === 0) {
    return {
      averageCycleLength: 28,
      nextPeriodDate: null,
      ovulationDate: null,
      isRegular: false,
      cycleVariability: 0
    }
  }

  // Sort records by start date
  const sortedRecords = [...records].sort((a, b) => {
    const dateA = a.start_date?.toDate() || new Date(0)
    const dateB = b.start_date?.toDate() || new Date(0)
    return dateA - dateB
  })

  // Calculate cycle lengths (days between period starts)
  const cycleLengths = []
  for (let i = 1; i < sortedRecords.length; i++) {
    const prevDate = sortedRecords[i - 1].start_date?.toDate()
    const currDate = sortedRecords[i].start_date?.toDate()

    if (prevDate && currDate) {
      const daysBetween = Math.floor((currDate - prevDate) / (24 * 60 * 60 * 1000))
      if (daysBetween >= 15 && daysBetween <= 45) { // Valid cycle range
        cycleLengths.push(daysBetween)
      }
    }
  }

  // Calculate average cycle length (use last 3-6 cycles if available)
  const recentCycles = cycleLengths.slice(-6)
  const averageCycleLength = recentCycles.length > 0
    ? Math.round(recentCycles.reduce((sum, len) => sum + len, 0) / recentCycles.length)
    : 28

  // Calculate cycle variability (standard deviation)
  let cycleVariability = 0
  if (recentCycles.length > 1) {
    const mean = averageCycleLength
    const squaredDiffs = recentCycles.map(len => Math.pow(len - mean, 2))
    const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / recentCycles.length
    cycleVariability = Math.sqrt(variance)
  }

  // Determine if cycle is regular (variability < 7 days)
  const isRegular = cycleVariability < 7

  // Predict next period date
  const lastPeriod = sortedRecords[sortedRecords.length - 1].start_date?.toDate()
  let nextPeriodDate = null
  let ovulationDate = null

  if (lastPeriod) {
    nextPeriodDate = new Date(lastPeriod)
    nextPeriodDate.setDate(nextPeriodDate.getDate() + averageCycleLength)

    // Calculate ovulation date (14 days before next period)
    ovulationDate = new Date(nextPeriodDate)
    ovulationDate.setDate(ovulationDate.getDate() - 14)
  }

  return {
    averageCycleLength,
    nextPeriodDate,
    ovulationDate,
    isRegular,
    cycleVariability: Math.round(cycleVariability * 10) / 10
  }
}

/**
 * Get fertile window dates (5 days before ovulation + ovulation day + 1 day after)
 */
export const getFertileWindow = (ovulationDate) => {
  if (!ovulationDate) return []

  const fertileWindow = []
  const startDate = new Date(ovulationDate)
  startDate.setDate(startDate.getDate() - 5)

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate)
    date.setDate(date.getDate() + i)
    fertileWindow.push(date)
  }

  return fertileWindow
}

// =============== User Settings ===============

/**
 * Get user menstrual settings
 */
export const getUserSettings = async (userId) => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, userId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return docSnap.data()
    }

    // Return default settings if none exist
    return {
      remind_period: true,
      remind_ovulation: true,
      email_notification: false,
      reminder_days_before: 3
    }
  } catch (error) {
    console.error('Error getting user settings:', error)
    throw error
  }
}

/**
 * Update user menstrual settings
 */
export const updateUserSettings = async (userId, settings) => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, userId)
    await updateDoc(docRef, {
      ...settings,
      updated_at: serverTimestamp()
    })
  } catch (error) {
    // If document doesn't exist, create it
    if (error.code === 'not-found') {
      await addDoc(collection(db, SETTINGS_COLLECTION), {
        user_id: userId,
        ...settings,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })
    } else {
      console.error('Error updating user settings:', error)
      throw error
    }
  }
}

// =============== Data Export ===============

/**
 * Export menstrual records to CSV format
 */
export const exportToCSV = (records) => {
  if (!records || records.length === 0) {
    return ''
  }

  // CSV headers
  const headers = ['Date', 'Start Date', 'End Date', 'Flow Level', 'Symptoms', 'Mood', 'Note']

  // CSV rows
  const rows = records.map(record => {
    const startDate = record.start_date?.toDate()?.toLocaleDateString() || ''
    const endDate = record.end_date?.toDate()?.toLocaleDateString() || ''
    const symptoms = Array.isArray(record.symptoms) ? record.symptoms.join('; ') : ''

    return [
      startDate,
      startDate,
      endDate,
      record.flow_level || '',
      symptoms,
      record.mood || '',
      (record.note || '').replace(/"/g, '""') // Escape quotes
    ]
  })

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n')

  return csvContent
}

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename = 'menstrual_records.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
